/**
 * Guest/authenticated buyer like mirror — listing IDs only, scoped per client view.
 * Cookie is the refresh-survival source; localStorage mirrors for merge-ready redundancy (M1).
 */

import { getCookie, setCookie } from "../layerCookieState";

export const BUYER_LIKES_COOKIE_DAYS = 90;
export const BUYER_LIKES_MAX_IDS = 50;
export const BUYER_LIKES_CHANGED_EVENT = "automap-buyer-likes-changed";
export const BUYER_LIKES_GUEST_PROMPT_EVENT = "automap-guest-like-prompt";

function storageKey(slug) {
  return `automap_likes:${String(slug || "").trim().toLowerCase()}`;
}

function cookieName(slug) {
  return `automap_likes_${String(slug || "").trim().toLowerCase()}`;
}

function normalizeListingId(listingId) {
  const id = String(listingId || "").trim();
  return id || null;
}

function parseIdList(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return [...new Set(parsed.map(normalizeListingId).filter(Boolean))].slice(0, BUYER_LIKES_MAX_IDS);
  } catch {
    return [];
  }
}

function readLocalStorageIds(slug) {
  if (typeof window === "undefined") return [];
  try {
    return parseIdList(window.localStorage.getItem(storageKey(slug)));
  } catch {
    return [];
  }
}

function writeLocalStorageIds(slug, ids) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(slug), JSON.stringify(ids));
  } catch {
    // ignore quota errors
  }
}

function readCookieIds(slug) {
  return parseIdList(getCookie(cookieName(slug)));
}

function writeCookieIds(slug, ids) {
  setCookie(cookieName(slug), JSON.stringify(ids), BUYER_LIKES_COOKIE_DAYS);
}

function persistIds(slug, ids) {
  const normalized = [...new Set(ids.map(normalizeListingId).filter(Boolean))].slice(
    0,
    BUYER_LIKES_MAX_IDS
  );
  writeCookieIds(slug, normalized);
  writeLocalStorageIds(slug, normalized);
  return normalized;
}

export function emitBuyerLikesChanged(detail = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(BUYER_LIKES_CHANGED_EVENT, { detail }));
}

export function emitGuestLikePrompt(detail = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(BUYER_LIKES_GUEST_PROMPT_EVENT, { detail }));
}

export function loadLikedVehicleIds(slug) {
  if (!slug) return [];
  const cookieIds = readCookieIds(slug);
  const localIds = readLocalStorageIds(slug);
  if (!cookieIds.length && !localIds.length) return [];
  const merged = [...new Set([...cookieIds, ...localIds])].slice(0, BUYER_LIKES_MAX_IDS);
  if (merged.length !== cookieIds.length || merged.some((id, index) => id !== cookieIds[index])) {
    persistIds(slug, merged);
  }
  return merged;
}

export function isVehicleLiked(slug, listingId) {
  const id = normalizeListingId(listingId);
  if (!slug || !id) return false;
  return loadLikedVehicleIds(slug).includes(id);
}

export function addLikedVehicleId(slug, listingId) {
  const id = normalizeListingId(listingId);
  if (!slug || !id) return loadLikedVehicleIds(slug);
  const next = [id, ...loadLikedVehicleIds(slug).filter((entry) => entry !== id)].slice(
    0,
    BUYER_LIKES_MAX_IDS
  );
  persistIds(slug, next);
  emitBuyerLikesChanged({ slug, listingId: id, liked: true, ids: next });
  return next;
}

export function removeLikedVehicleId(slug, listingId) {
  const id = normalizeListingId(listingId);
  if (!slug || !id) return loadLikedVehicleIds(slug);
  const next = loadLikedVehicleIds(slug).filter((entry) => entry !== id);
  persistIds(slug, next);
  emitBuyerLikesChanged({ slug, listingId: id, liked: false, ids: next });
  return next;
}

export function toggleLikedVehicleId(slug, listingId) {
  if (isVehicleLiked(slug, listingId)) {
    return { liked: false, ids: removeLikedVehicleId(slug, listingId) };
  }
  return { liked: true, ids: addLikedVehicleId(slug, listingId) };
}

export function clearLikedVehicleIds(slug) {
  if (!slug) return [];
  persistIds(slug, []);
  emitBuyerLikesChanged({ slug, listingId: null, liked: false, ids: [] });
  return [];
}
